export type MetricRow = Record<string, unknown>;

export const isRecord = (value: unknown): value is MetricRow =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const hasRows = (value: unknown): value is MetricRow & { rows: unknown[] } =>
  isRecord(value) && Array.isArray(value.rows);

export const formatLeaf = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const prettifyKey = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (character) => character.toUpperCase());

export const metricValueToRows = (value: unknown, scalarLabel: string): MetricRow[] => {
  if (hasRows(value)) return value.rows.filter(isRecord);
  if (Array.isArray(value)) {
    if (value.every(hasRows)) return value.flatMap((section) => section.rows.filter(isRecord));
    if (value.every(isRecord)) return value;
    return [{ [scalarLabel]: value.map(formatLeaf).join(', ') }];
  }
  if (isRecord(value)) return [value];
  return [{ [scalarLabel]: value }];
};

export const columnsOf = (rows: MetricRow[]): string[] => {
  const columns: string[] = [];
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!columns.includes(key)) columns.push(key);
    });
  });
  return columns;
};

export const rowsToTsv = (rows: MetricRow[]): string => {
  const columns = columnsOf(rows);
  return [
    columns.map(prettifyKey).join('\t'),
    ...rows.map((row) => columns.map((key) => formatLeaf(row[key])).join('\t')),
  ].join('\n');
};
